/**
 * CoverageRadarStrip — Jul 2026.
 *
 * Two compact Dashboard teaser cards cross-referencing the user's
 * WATCHLIST with the SEC XBRL coverage radars:
 *   • Cut-Risk Radar  (rose)    — coverage < 1.0× and deteriorating
 *   • Recovery Radar (emerald) — coverage ≥ 1.0× and rising > +10%
 * Each ticker chip opens the Stock Modal; "View all" deep-links into
 * the matching Screener preset. Phone-first: chips wrap, cards stack
 * on small screens, and everything is tap-driven (no hover-only UI).
 *
 * Null-renders when: logged out, watchlist empty, or both radars empty.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { TrendingDown, TrendingUp, ChevronRight, Info } from 'lucide-react';
import { coverageRatioClass } from '@/components/CoverageTip';
import CoverageSparkline from '@/components/CoverageSparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RadarCard({ tone, icon: Icon, title, subtitle, items, presetHref, testId, onOpenStock, expanded = false, footerLinkLabel = null }) {
  const isRose = tone === 'rose';
  const shown = expanded ? items : items.slice(0, 5);
  // Feb 2026 — Sparkline explainer expands on tap. Kept collapsed by
  // default so it doesn't clutter the card, but a first-time visitor
  // can figure out what the tiny 4-dot chart is showing.
  const [sparkHelpOpen, setSparkHelpOpen] = useState(false);
  const hasSparklines = expanded && shown.some((it) => Array.isArray(it.history) && it.history.length >= 2);
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${isRose
        ? 'border-rose-300/50 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-950/20'
        : 'border-emerald-300/50 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-950/20'}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon className={`w-3.5 h-3.5 shrink-0 ${isRose ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
          <span className="text-[11px] font-bold uppercase tracking-wider truncate">
            {title}
            <span className={`ml-1.5 font-mono ${isRose ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {items.length}
            </span>
          </span>
        </div>
        {!expanded && (
          <Link
            to={presetHref}
            className={`inline-flex items-center gap-0.5 text-[10px] font-bold shrink-0 hover:underline ${isRose ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            data-testid={`${testId}-view-all`}
          >
            View all<ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</p>
      {/* Feb 2026 — Sparkline legend. Explains the 4-dot chart that
          renders inside each ticker chip below so a first-time
          visitor knows exactly how the "recovering" / "failing"
          call was made. Toggle so it stays out of the way once
          understood. */}
      {hasSparklines && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setSparkHelpOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`${testId}-sparkline-help-toggle`}
          >
            <Info className="w-2.5 h-2.5" />
            About the sparkline
          </button>
          {sparkHelpOpen && (
            <p className="mt-1 text-[10px] leading-snug text-muted-foreground rounded border border-border/40 bg-background/60 dark:bg-background/40 px-2 py-1.5">
              Each dot is one <span className="font-semibold">SEC-filed quarter</span> of coverage
              ({isRose ? 'FCF or Net Income' : 'FCF or Net Income'} ÷ Dividends).
              Left → right = oldest → newest. The dashed line marks the
              <span className="font-mono"> 1.0×</span> floor — coverage below it means
              the payout is outrunning cash flow. Dot color = tier at
              that quarter (<span className="text-rose-600 dark:text-rose-400 font-semibold">red</span> below 1.0×,{' '}
              <span className="text-amber-600 dark:text-amber-400 font-semibold">amber</span> 1.0–1.5×,{' '}
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">green</span> ≥1.5×).
              {isRose
                ? ' Line trending down = deteriorating — the classic pre-cut setup.'
                : ' Line trending up while dots stay green = the setup before a raise.'}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {shown.map((it) => (
          expanded ? (
            // Round 105h — big two-line pill: everything the desktop
            // hover title used to hide is now printed on the pill.
            // Feb 2026 — sparkline slots in below the trend row so
            // the trajectory reads without an extra tap.
            <button
              key={it.ticker}
              type="button"
              onClick={() => onOpenStock?.(it.ticker)}
              className="flex flex-col items-start rounded-lg border bg-background/80 px-2.5 py-1.5 text-[11px] font-mono hover:bg-muted transition-colors"
              data-testid={`coverage-radar-chip-${it.ticker}`}
            >
              <span>
                <span className="font-bold">{it.ticker}</span>{' '}
                <span className={coverageRatioClass(Number(it.ratio))}>
                  {Number(it.ratio).toFixed(2)}×
                </span>{' '}
                <span className="text-muted-foreground">{it.metric} coverage</span>
              </span>
              {it.trend_pct != null && (
                <span className={`text-[10px] ${Number(it.trend_pct) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  Trend {Number(it.trend_pct) > 0 ? '+' : ''}{Number(it.trend_pct).toFixed(1)}%
                </span>
              )}
              {Array.isArray(it.history) && it.history.length >= 2 && (
                <span
                  className={`mt-0.5 inline-flex items-center gap-1 ${isRose ? 'text-rose-600/60 dark:text-rose-400/60' : 'text-emerald-600/60 dark:text-emerald-400/60'}`}
                  title={`Last ${it.history.length} SEC-filed quarters — oldest → newest`}
                >
                  <CoverageSparkline history={it.history} tone={tone} />
                  {(() => {
                    // Feb 2026 — Q-over-Q ratio delta arrow. Reads the
                    // final two ratios in `history` (already ordered
                    // oldest → newest) and surfaces the raw absolute
                    // delta so a glance tells the user which way
                    // coverage moved. `history` items include a
                    // `ratio` (number) — anything else is ignored.
                    const h = it.history;
                    const last = Number(h[h.length - 1]?.ratio);
                    const prev = Number(h[h.length - 2]?.ratio);
                    if (!Number.isFinite(last) || !Number.isFinite(prev)) return null;
                    const delta = last - prev;
                    const sign = delta >= 0 ? '+' : '';
                    const arrow = delta > 0 ? '↑' : (delta < 0 ? '↓' : '→');
                    const arrowCls = delta > 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : (delta < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground');
                    return (
                      <span
                        className={`text-[9px] font-mono font-semibold ${arrowCls}`}
                        title={`Q-over-Q coverage delta: ${sign}${delta.toFixed(2)}× (${prev.toFixed(2)}× → ${last.toFixed(2)}×)`}
                        data-testid={`coverage-radar-trend-arrow-${it.ticker}`}
                      >
                        {arrow}{sign}{delta.toFixed(2)}×
                      </span>
                    );
                  })()}
                </span>
              )}
            </button>
          ) : (
            <button
              key={it.ticker}
              type="button"
              onClick={() => onOpenStock?.(it.ticker)}
              className="inline-flex items-center gap-1 rounded-full border bg-background/80 px-2 py-0.5 text-[11px] font-mono hover:bg-muted transition-colors"
              data-testid={`coverage-radar-chip-${it.ticker}`}
              title={`${it.ticker}: ${Number(it.ratio).toFixed(2)}× ${it.metric} coverage${it.trend_pct != null ? `, trend ${it.trend_pct > 0 ? '+' : ''}${Number(it.trend_pct).toFixed(1)}%` : ''}`}
            >
              <span className="font-bold">{it.ticker}</span>
              <span className={coverageRatioClass(Number(it.ratio))}>
                {Number(it.ratio).toFixed(2)}×
              </span>
            </button>
          )
        ))}
        {!expanded && items.length > 5 && (
          <Link
            to={presetHref}
            className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-[11px] font-mono text-muted-foreground hover:bg-muted transition-colors"
          >
            +{items.length - 5} more
          </Link>
        )}
      </div>
      {expanded && footerLinkLabel && (
        <div className="mt-2 text-center">
          <Link
            to={presetHref}
            className={`inline-flex items-center gap-0.5 text-[11px] font-bold hover:underline ${isRose ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            data-testid={`${testId}-view-all`}
          >
            {footerLinkLabel}<ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

export default function CoverageRadarStrip({ onOpenStock, stacked = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/dashboard/coverage-radar`, { withCredentials: true })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const cut = data.cut_risk || [];
  const rec = data.recovery || [];
  if (cut.length === 0 && rec.length === 0) return null;

  return (
    <div
      className={
        stacked
          // P126 (Feb 2026): "stacked" layout used on the Watchlist
          // view — each card gets its own full-width row so it
          // visually matches the Watchlist Snapshot card sitting
          // right below it. Recovery card renders FIRST (user
          // ordered it that way — the positive signal above the
          // warning signal).
          ? "grid grid-cols-1 gap-3 mt-2 mb-3"
          : "grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 mb-3"
      }
      data-testid="coverage-radar-strip"
    >
      {stacked && rec.length > 0 && (
        <RadarCard
          tone="emerald"
          icon={TrendingUp}
          title="Watchlist on Recovery Radar"
          subtitle="Coverage above 1.0× and rising fast — the setup before a raise."
          items={rec}
          presetHref="/screener?preset=coverage_improving"
          testId="coverage-radar-recovery"
          onOpenStock={onOpenStock}
          expanded
          footerLinkLabel="Dividend Coverage Improving - View All in Screener"
        />
      )}
      {cut.length > 0 && (
        <RadarCard
          tone="rose"
          icon={TrendingDown}
          title="Watchlist on Cut-Risk Radar"
          subtitle="SEC-reported coverage below 1.0× and deteriorating — the classic pre-cut setup."
          items={cut}
          presetHref="/screener?preset=coverage_watch"
          testId="coverage-radar-cutrisk"
          onOpenStock={onOpenStock}
          expanded
          footerLinkLabel="Dividend Coverage At Risk - View All in Screener"
        />
      )}
      {!stacked && rec.length > 0 && (
        <RadarCard
          tone="emerald"
          icon={TrendingUp}
          title="Watchlist on Recovery Radar"
          subtitle="Coverage above 1.0× and rising fast — the setup before a raise."
          items={rec}
          presetHref="/screener?preset=coverage_improving"
          testId="coverage-radar-recovery"
          onOpenStock={onOpenStock}
          expanded
          footerLinkLabel="Dividend Coverage Improving - View All in Screener"
        />
      )}
    </div>
  );
}
